import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../common/layout";
import { useTranslation } from "react-i18next";
import { Container } from "../../common/layout/styels";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ListTable from "../../../elements/list/list";
import Loader from "../../../elements/loader";
import { getData, postData } from "../../../../backend/api";
import { GetAccessToken } from "../../../../backend/authentication";
import axios from "axios";
import useBillingApi from "../billing/useBillingApi";
import ProductSelect from "../billing/components/ProductSelect";
import {
  CreateFormContainer,
  FormSection,
  SectionTitle,
  FormRow,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextArea,
  ItemsHeader,
  ItemCard,
  ItemNumber,
  AddItemButton,
  RemoveItemButton,
  TotalsSection,
  TotalRow,
  TotalLabel,
  TotalValue,
  TotalDivider,
  GrandTotalLabel,
  GrandTotalValue,
  TaxNote,
  SubmitButton,
  BackButton,
  FormHeader,
  ButtonRow,
} from "./styels";
import { GetIcon } from "../../../../icons";
import Decimal from "decimal.js";

const getInvoiceList = (responseData) => {
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const getLatestInvoiceForCompany = (responseData, companyId) => {
  const invoices = getInvoiceList(responseData)
    .filter((invoice) => {
      const invoiceCompanyId = String(
        invoice?.companyId?._id || invoice?.companyId || ""
      );
      return invoiceCompanyId === String(companyId);
    })
    .sort((left, right) => {
      const leftDate = new Date(
        left?.createdAt || left?.updatedAt || left?.invoiceDate || left?.issuedAt || 0
      ).getTime();
      const rightDate = new Date(
        right?.createdAt || right?.updatedAt || right?.invoiceDate || right?.issuedAt || 0
      ).getTime();

      return rightDate - leftDate;
    });

  return invoices[0] || null;
};

const Invoice = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const billingApi = useBillingApi();

  const [showLoader, setShowLoader] = useState(false);
  const [view, setView] = useState("list"); // list | create
  const [activeTab, setActiveTab] = useState("invoices"); // invoices | templates
  const [companies, setCompanies] = useState([]);
  const [refetchTableData, setRefetchTableData] = useState(false);

  const { setMessage } = props;

  // ── List view attributes for ListTable ──
  const [attributes] = useState([
    {
      type: "text",
      placeholder: "invoiceNumber",
      name: "invoiceNumber",
      validation: "",
      default: "",
      label: "invoiceNumber",
      required: false,
      view: true,
      add: false,
      update: false,
    },
    {
      type: "select",
      placeholder: "company",
      name: "companyId",
      validation: "",
      showItem: "organization",
      default: "",
      label: "company",
      required: true,
      view: true,
      add: false,
      update: false,
      filter: false,
      apiType: "API",
      selectApi: "company/select",
    },
    {
      type: "select",
      placeholder: "status",
      name: "status",
      validation: "",
      default: "draft",
      label: "status",
      required: false,
      view: true,
      add: false,
      update: true,
      filter: false,
      apiType: "JSON",
      selectApi: [
        { id: "draft", value: t("draft") },
        { id: "sent", value: t("sent") },
        { id: "paid", value: t("paid") },
        { id: "overdue", value: t("overdue") },
        { id: "void", value: t("void") },
      ],
    },
    {
      type: "number",
      placeholder: "totalAmount",
      name: "totalAmount",
      validation: "",
      default: "",
      label: "totalAmount",
      required: false,
      view: true,
      tag: true,
      add: false,
      update: false,
      // currency: true
    },
    {
      type: "text",
      placeholder: "currency",
      name: "currency",
      validation: "",
      default: "EUR",
      label: "currency",
      required: false,
      view: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      placeholder: "dueDate",
      name: "dueDate",
      validation: "",
      default: "",
      label: "dueDate",
      required: false,
      view: true,
      add: false,
      update: true,
    },
    // {
    //   type: "text",
    //   placeholder: "lexwareInvoiceNumber",
    //   name: "lexwareInvoiceNumber",
    //   validation: "",
    //   default: "",
    //   label: "lexwareInvoiceNumber",
    //   required: false,
    //   view: true,
    //   add: false,
    //   update: false,
    // },
    {
      type: "select",
      name: "type",
      label: "invoiceType",
      view: true,
      tag: true,
      add: false,
      update: false,
      translation: true,
      filter: true,
      apiType: "JSON",
      selectApi: [
        { id: "manual", value: t("manual") || "Manual" },
        { id: "subscription", value: t("subscription") || "Subscription" },
        { id: "recurring", value: t("recurring") || "Recurring" },
        { id: "creditNote", value: t("creditNote") || "Credit Note" },
      ],
    },
    {
      type: "number",
      name: "subtotal",
      label: "subtotal",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "number",
      name: "taxAmount",
      label: "tax",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "text",
      name: "paymentMethod",
      label: "paymentMethod",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      name: "paymentDate",
      label: "paymentDate",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "text",
      name: "notes",
      label: "notes",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      name: "createdAt",
      label: "createdAt",
      view: true,
      tag: true,
      add: false,
      update: false,
    },
  ]);
  const [companyAttributes] = useState([
    {
      type: "text",
      placeholder: "invoiceNumber",
      name: "invoiceNumber",
      validation: "",
      default: "",
      label: "invoiceNumber",
      required: false,
      view: true,
      add: false,
      update: false,
    },
    {
      type: "select",
      placeholder: "status",
      name: "status",
      validation: "",
      default: "draft",
      label: "status",
      required: false,
      view: true,
      add: false,
      update: true,
      filter: false,
      apiType: "JSON",
      selectApi: [
        { id: "draft", value: t("draft") },
        { id: "sent", value: t("sent") },
        { id: "paid", value: t("paid") },
        { id: "overdue", value: t("overdue") },
        { id: "void", value: t("void") },
      ],
    },
    {
      type: "number",
      placeholder: "totalAmount",
      name: "totalAmount",
      validation: "",
      default: "",
      label: "totalAmount",
      required: false,
      view: true,
      tag: true,
      add: false,
      update: false,
      // currency: true
    },
    {
      type: "text",
      placeholder: "currency",
      name: "currency",
      validation: "",
      default: "EUR",
      label: "currency",
      required: false,
      view: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      placeholder: "dueDate",
      name: "dueDate",
      validation: "",
      default: "",
      label: "dueDate",
      required: false,
      view: true,
      add: false,
      update: true,
    },
    // {
    //   type: "text",
    //   placeholder: "lexwareInvoiceNumber",
    //   name: "lexwareInvoiceNumber",
    //   validation: "",
    //   default: "",
    //   label: "lexwareInvoiceNumber",
    //   required: false,
    //   view: true,
    //   add: false,
    //   update: false,
    // },
    {
      type: "select",
      name: "type",
      label: "invoiceType",
      view: true,
      tag: true,
      add: false,
      update: false,
      translation: true,
      filter: true,
      apiType: "JSON",
      selectApi: [
        { id: "manual", value: t("manual") || "Manual" },
        { id: "subscription", value: t("subscription") || "Subscription" },
        { id: "recurring", value: t("recurring") || "Recurring" },
        { id: "creditNote", value: t("creditNote") || "Credit Note" },
      ],
    },
    {
      type: "number",
      name: "subtotal",
      label: "subtotal",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "number",
      name: "taxAmount",
      label: "tax",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "text",
      name: "paymentMethod",
      label: "paymentMethod",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      name: "paymentDate",
      label: "paymentDate",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "text",
      name: "notes",
      label: "notes",
      view: false,
      tag: true,
      add: false,
      update: false,
    },
    {
      type: "date",
      name: "createdAt",
      label: "createdAt",
      view: true,
      tag: true,
      add: false,
      update: false,
    },
  ]);
  
  const [templateAttributes] = useState([
    {
      type: "select",
      name: "companyId",
      label: "company",
      showItem: "organization",
      view: true,
      filter: true,
      apiType: "API",
      selectApi: "company/select",
    },
    {
      type: "select",
      name: "interval",
      label: "interval",
      view: true,
      tag: true,
      translation: true,
      apiType: "JSON",
      selectApi: [
        { id: "monthly", value: t("monthly") },
        { id: "quarterly", value: t("quarterly") },
        { id: "yearly", value: t("yearly") },
        { id: "custom_days", value: t("custom") },
      ],
    },
    {
      type: "date",
      name: "nextRunDate",
      label: "nextInvoiceDate",
      view: true,
    },
    {
      type: "select",
      placeholder: "status",
      name: "active",
      label: "status",
      view: true,
      filter: false,
      tag: true,
      add: false,
      update: false,
      apiType: "JSON",
      selectApi: [
        { id: true, value: t("active"), color: "green" },
        { id: false, value: t("paused"), color: "orange" },
      ],
    },
    {
      type: "text",
      name: "currency",
      label: "currency",
      view: true,
    },
  ]);

  // ── Create form state ──
  const emptyItem = {
    itemType: "manual",
    productId: "",
    productPriceId: "",
    description: "",
    quantity: "1",
    unitPrice: "",
    taxRate: "19",
  };

  const [form, setForm] = useState({
    companyId: "",
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    postalCode: "",
    country: "Germany",
    dueDate: "",
    notes: "",
    currency: "EUR",
    e_invoice: false,
    vatId: "",
    buyerReference: "",
    vendorNumberAtCustomer: "",
    type: "manual",
    linkedInvoiceId: "",
    recurringInterval: null,
    intervalDays: null,
    items: [{ ...emptyItem }],
  });

  // ── Page title ──
  useEffect(() => {
    document.title = `${t("invoices")} - PersoBase`;
  }, [t]);

  // ── Fetch companies for the select dropdown ──
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getData(
          {},
          "company/select",
          dispatch,
          navigate
        );
        if (response.status === 200 && response.data) {
          setCompanies(
            Array.isArray(response.data) ? response.data : response.data.data || []
          );
        }
      } catch (err) {
        console.error("Error fetching companies:", err);
      }
    };
    if (view === "create") {
      fetchCompanies();
    }
  }, [view, dispatch, navigate]);

  // ── Auto-populate billing info when company changes ──
  const handleCompanyChange = useCallback(
    async (companyId) => {
      setForm((prev) => ({ ...prev, companyId }));
      if (!companyId) return;

      try {
        const [companyResponse, invoiceResponse] = await Promise.all([
          getData({ id: companyId }, "company", dispatch, navigate),
          getData(
            { companyId, skip: 0, limit: 50 },
            "invoice",
            dispatch,
            navigate
          ),
        ]);

        if (companyResponse.status === 200 && companyResponse.data) {
          const company = Array.isArray(companyResponse.data.data)
            ? companyResponse.data.data[0]
            : companyResponse.data;
          const latestInvoice =
            invoiceResponse.status === 200
              ? getLatestInvoiceForCompany(invoiceResponse.data, companyId)
              : null;
          const billingInfo = latestInvoice?.billingInfo || {};
          const address = billingInfo.address || {};

          setForm((prev) => ({
            ...prev,
            companyId,
            companyName:
              billingInfo.companyName || company.organization || "",
            firstName: billingInfo.firstName || company.firstName || "",
            lastName: billingInfo.lastName || company.lastName || "",
            email: billingInfo.email || company.email || "",
            phone: billingInfo.phone || company.mobileNumber || "",
            street:
              address.street ||
              [company.streetNumber, company.houseNumber]
                .filter(Boolean)
                .join(" ") ||
              "",
            city: address.city || company.city || "",
            postalCode: address.postalCode || company.postalCode || "",
            country: address.country || (company.country?.de || company.country?.en || (typeof company.country === "string" ? company.country : "")) || "Germany",
            vatId: billingInfo.vatId || company.vatId || "",
            buyerReference: billingInfo.buyerReference || "",
            vendorNumberAtCustomer:
              billingInfo.vendorNumberAtCustomer || "",
            e_invoice:
              latestInvoice?.e_invoice !== undefined
                ? Boolean(latestInvoice.e_invoice)
                : false,
            currency: latestInvoice?.currency || prev.currency || "EUR",
          }));
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    },
    [dispatch, navigate]
  );

  // ── Form field handlers ──
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const handleItemTypeChange = (index, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        itemType: value,
        productId: value === "product" ? items[index].productId || "" : "",
        productPriceId:
          value === "product" ? items[index].productPriceId || "" : "",
      };
      return { ...prev, items };
    });
  };

  const handleProductResolved = (index, resolvedProduct) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        productId: resolvedProduct.productId || items[index].productId,
        productPriceId:
          resolvedProduct.productPriceId || items[index].productPriceId || "",
        description: resolvedProduct.description || items[index].description,
        unitPrice:
          resolvedProduct.unitPrice !== undefined
            ? String(resolvedProduct.unitPrice)
            : items[index].unitPrice,
        taxRate:
          resolvedProduct.taxRate !== undefined
            ? String(resolvedProduct.taxRate)
            : items[index].taxRate,
      };
      return { ...prev, items };
    });
    if (resolvedProduct.currency) {
      updateField("currency", resolvedProduct.currency);
    }
  };

  const handleProductResolveError = (response) => {
    setMessage({
      type: 1,
      content: t(response.data) || response.data || "serverError",
      proceed: "Okay",
      code: response.status || 500,
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const handleRecurringToggle = (checked) => {
    setForm((prev) => ({
      ...prev,
      recurringInterval: checked ? "" : null,
      intervalDays: null,
    }));
  };

  const handleRecurringIntervalChange = (value) => {
    setForm((prev) => ({
      ...prev,
      recurringInterval: value,
      intervalDays: value === "custom_days" ? prev.intervalDays ?? "" : null,
    }));
  };

  const handleIntervalDaysChange = (value) => {
    if (!/^\d*$/.test(value)) return;
    updateField("intervalDays", value);
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // ── Calculated totals ──
  const calculateItemTotal = (item) => {
    const qty = new Decimal(item.quantity || 0);
    const price = new Decimal(item.unitPrice || 0);

    return qty.mul(price);
  };

  const subtotal = form.items.reduce((sum, item) => {
    return sum.plus(calculateItemTotal(item));
  }, new Decimal(0));

  const taxAmount = form.items.reduce((sum, item) => {
    const lineTotal = calculateItemTotal(item);
    const taxRate = new Decimal(item.taxRate || 0);

    const tax = lineTotal.mul(taxRate).div(100);

    return sum.plus(tax);
  }, new Decimal(0));

  const totalAmount = subtotal.plus(taxAmount);

  // ── Submit handler ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.companyId) {
      setMessage({
        type: 1,
        content: t("pleaseSelectCompany") || "Please select a company",
        proceed: "Okay",
        code: 400,
      });
      return;
    }
    if (
      form.items.some(
        (item) => item.itemType === "product" && !item.productId
      )
    ) {
      setMessage({
        type: 1,
        content:
          t("pleaseSelectProductForAllProductItems") ||
          "Please select a product for all product items.",
        proceed: "Okay",
        code: 400,
      });
      return;
    }
    if (form.items.some((item) => !item.description)) {
      setMessage({
        type: 1,
        content:
          t("pleaseAddItemDescription") ||
          "Please add a description for all items",
        proceed: "Okay",
        code: 400,
      });
      return;
    }
    if (form.e_invoice && !form.buyerReference) {
      setMessage({
        type: 1,
        content:
          t("buyerReferenceRequiredForEInvoice") ||
          "Buyer Reference is required for E-Invoices.",
        proceed: "Okay",
        code: 400,
      });
      return;
    }
    if (form.recurringInterval !== null && !form.recurringInterval) {
      setMessage({
        type: 1,
        content:
          t("pleaseSelectRecurringInterval") ||
          "Please select a recurring interval.",
        proceed: "Okay",
        code: 400,
      });
      return;
    }
    if (
      form.recurringInterval === "custom_days" &&
      (!form.intervalDays || Number(form.intervalDays) <= 0)
    ) {
      setMessage({
        type: 1,
        content:
          t("pleaseEnterValidIntervalDays") ||
          "Please enter valid interval days greater than 0.",
        proceed: "Okay",
        code: 400,
      });
      return;
    }

    setShowLoader(true);
    try {
      const payload = {
        companyId: form.companyId,
        items: form.items.map((item) => ({
          productId: item.productId || undefined,
          description: item.description,
          quantity: new Decimal(item.quantity || 1).toNumber(),
          unitPrice: new Decimal(item.unitPrice || 0).toNumber(),
          taxRate: new Decimal(item.taxRate || 19).toNumber(),
        })),
        dueDate: form.dueDate || undefined,
        notes: form.notes || undefined,
        currency: form.currency || "EUR",
        e_invoice: form.e_invoice,
        type: form.type || "manual",
        linkedInvoiceId: form.type === "creditNote" ? form.linkedInvoiceId : undefined,
        recurringInterval:
          form.recurringInterval !== null && form.recurringInterval
            ? form.recurringInterval
            : undefined,
        intervalDays:
          form.recurringInterval === "custom_days" && form.intervalDays
            ? Number(form.intervalDays)
            : undefined,
        billingInfo: {
          companyName: form.companyName,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          address: {
            street: form.street,
            city: form.city,
            postalCode: form.postalCode,
            country: form.country,
          },
          vatId: form.vatId || undefined,
          buyerReference: form.buyerReference || undefined,
          vendorNumberAtCustomer: form.vendorNumberAtCustomer || undefined,
        },
      };

      const response = await postData(payload, "invoice", dispatch, navigate);

      if (response.status === 200 || response.status === 201) {
        // Reset form and go back to list
        setForm({
          companyId: "",
          companyName: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          street: "",
          city: "",
          postalCode: "",
          country: "Germany",
          dueDate: "",
          notes: "",
          currency: "EUR",
          e_invoice: false,
          vatId: "",
          buyerReference: "",
          vendorNumberAtCustomer: "",
          type: "manual",
          linkedInvoiceId: "",
          recurringInterval: null,
          intervalDays: null,
          items: [{ ...emptyItem }],
        });
        setMessage({
          type: 1,
          content: t("created", { shortName: t("invoice") }),
          proceed: "Okay",
          code: 200,
        });
        setView("list");
      } else {
        setMessage({
            type: 1,
            content: t(response.data),
            proceed: "Okay",
            code: 500,
          });
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      setMessage({
        type: 1,
        content: t("serverError") || "Error creating invoice",
        proceed: "Okay",
        code: 500,
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleToggleTemplateActive = async (rowData) => {
    try {
      setShowLoader(true);
      const response = await billingApi.updateRecurringInvoice(rowData._id, {
        active: !rowData.active,
      });
      if (response.status === 200) {
        triggerReload();
        setMessage({
          type: 1,
          content: t("updated", { shortName: t("recurringInvoice") }),
          proceed: "Okay",
          code: 200,
        });
      }
    } catch (err) {
      console.error("Error toggling template state:", err);
    } finally {
      setShowLoader(false);
    }
  };

  const handleDeleteTemplate = async (rowData) => {
    try {
      setShowLoader(true);
      const response = await billingApi.deleteRecurringInvoice(rowData._id);
      if (response.status === 403) {
        setMessage({
          type: 1,
          content: t("forbidden") || "Permission denied",
          proceed: "Okay",
          code: 403,
        });
      }
      if (response.status === 200) {
        triggerReload();
        setMessage({
          type: 1,
          content: t("deleted", { shortName: t("recurringInvoice") }),
          proceed: "Okay",
          code: 200,
        });
      }
    } catch (err) {
      console.error("Error deleting template:", err);
    } finally {
      setShowLoader(false);
    }
  };

  const triggerReload = useCallback(() => {
    clearTimeout(window.__reloadTimer);
    window.__reloadTimer = setTimeout(() => {
      setRefetchTableData(prev => !prev);
    }, 300);
  }, []);

  const formatCurrency = (amount, currencyCode) => {
    const value =
      amount instanceof Decimal ? amount.toNumber() : Number(amount || 0);

    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currencyCode || form.currency || "EUR",
    }).format(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  };

  const handleNumberChange = (e) => {
    const raw = e.target.value;

    // Allow only numbers and decimals
    if (!/^\d*\.?\d*$/.test(raw)) return null;

    return raw;
  };

  const handleBlurQuantity = (value) => {
    if (!value) return "0.01";

    const num = new Decimal(value);

    if (num.lessThan(0.01)) {
      return "0.01";
    }

    return num.toDecimalPlaces(4).toString();
  };

  const handleDownload = async (rowData) => {
    try {
      setShowLoader(true);
      const token = GetAccessToken();
      const id = rowData._id || rowData.id;
      
      const response = await axios.get(
        `${process.env.REACT_APP_API}invoice/${id}/download?lang=${localStorage.getItem("_lang") || "de"}`,
        {
          headers: {
            "x-access-token": token,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Fallback filename depends on e_invoice status
      const defaultExtension = rowData.e_invoice ? "zip" : "pdf";
      let fileName = `${rowData.invoiceNumber || "invoice"}.${defaultExtension}`;
      
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const nameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (nameMatch && nameMatch.length === 2) {
          fileName = nameMatch[1];
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      let errorMessage = t("downloadError") || "Error downloading invoice.";

      // Blob response type causes error responses to also be blobs — parse it back to JSON
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json?.message) errorMessage = json.message;
        } catch {
          // Blob wasn't JSON, fall back to default message
        }
      }

      setMessage({
        type: 1,
        content: errorMessage,
        proceed: "Okay",
        code: error.response?.status || 500,
      });
    } finally {
      setShowLoader(false);
    }
  };

  // ── Render CREATE view ──
  if (view === "create") {
    return (
      <Container className="noshadow">
        <CreateFormContainer onSubmit={handleSubmit}>
          {/* ── Header ── */}
          <FormHeader>
            <BackButton type="button" onClick={() => setView("list")}>
              ← {t("back")}
            </BackButton>
            <h2>{t("createInvoice") || "Create Invoice"}</h2>
          </FormHeader>

          {/* ── Billing Information ── */}
          <FormSection>
            <SectionTitle>{t("billingInformation") || "Billing Information"}</SectionTitle>
            <FormRow>
              <FormGroup className="full">
                <FormLabel>{t("companyName") || "Company Name"}</FormLabel>
                <FormSelect
                  value={form.companyId}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  required
                >
                  <option value="">{t("selectCompany") || "Select a company..."}</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.value}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <FormLabel className="required">{t("firstName") || "First Name"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.firstName}
                  onChange={(e) => updateField("firstName", e.target.value)}
                  placeholder={t("firstName") || "First Name"}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel className="required">{t("lastName") || "Last Name"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.lastName}
                  onChange={(e) => updateField("lastName", e.target.value)}
                  placeholder={t("lastName") || "Last Name"}
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <FormLabel className="required">{t("email") || "Email"}</FormLabel>
                <FormInput
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="john.doe@example.com"
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>{t("phone") || "Phone"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder="+49 123 456789"
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup className="full">
                <FormLabel className="required">{t("streetAddress") || "Street Address"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.street}
                  onChange={(e) => updateField("street", e.target.value)}
                  placeholder={t("streetAddress") || "123 Main Street"}
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <FormLabel className="required">{t("city") || "City"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  placeholder={t("city") || "Berlin"}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel className="required">{t("postalCode") || "Postal Code"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.postalCode}
                  onChange={(e) => updateField("postalCode", e.target.value)}
                  placeholder="10115"
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <FormLabel className="required">{t("country") || "Country"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.country}
                  onChange={(e) => updateField("country", e.target.value)}
                  placeholder={t("country") || "Germany"}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>{t("vatId") || "VAT ID"}</FormLabel>
                <FormInput
                  type="text"
                  value={form.vatId}
                  onChange={(e) => updateField("vatId", e.target.value)}
                  placeholder={t("vatIdPlaceholder") || "e.g. DE123456789"}
                />
              </FormGroup>
            </FormRow>
          </FormSection>

          {/* ── Basic Information ── */}
          <FormSection>
            <SectionTitle>{t("basicInformation") || "Basic Information"}</SectionTitle>
            <FormRow>
              <FormGroup>
                <FormLabel>{t("dueDate") || "Due Date"}</FormLabel>
                <FormSelect
                  value={form.dueDate ? "custom" : "immediate"}
                  onChange={(e) => {
                    if (e.target.value === "immediate") {
                      updateField("dueDate", "");
                    } else {
                      updateField("dueDate", new Date().toISOString().split("T")[0]);
                    }
                  }}
                >
                  <option value="immediate">{t("immediate") || "Immediate"}</option>
                  <option value="custom">{t("customDate") || "Custom Date"}</option>
                </FormSelect>
                {form.dueDate && (
                  <FormInput
                    type="date"
                    value={form.dueDate}
                    onChange={(e) => updateField("dueDate", e.target.value)}
                    style={{ marginTop: "8px" }}
                  />
                )}
                <TaxNote>{t("immediateDueDateNote") || "Immediate issues the invoice without a due date."}</TaxNote>
              </FormGroup>
              <FormGroup>
                <FormLabel>{t("E-Invoice")}</FormLabel>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", marginTop: "8px" }}>
                  <input
                    type="checkbox"
                    checked={form.e_invoice}
                    onChange={(e) => updateField("e_invoice", e.target.checked)}
                  />
                  {t("enableEInvoice") || "Enable e-invoice (XML + PDF)"}
                </label>
              </FormGroup>
              <FormGroup>
                <FormLabel>{t("invoiceType") || "Invoice Type"}</FormLabel>
                <FormSelect
                  value={form.type}
                  onChange={(e) => updateField("type", e.target.value)}
                >
                  <option value="manual">{t("manual") || "Manual"}</option>
                  <option value="creditNote">{t("creditNote") || "Credit Note"}</option>
                </FormSelect>
              </FormGroup>
            </FormRow>
            
            {form.type === "creditNote" && (
              <FormRow>
                <FormGroup className="full">
                  <FormLabel className="required">{t("linkedInvoiceId") || "Linked Invoice ID"}</FormLabel>
                  <FormInput
                    type="text"
                    value={form.linkedInvoiceId || ""}
                    onChange={(e) => updateField("linkedInvoiceId", e.target.value)}
                    placeholder="Enter original invoice _id"
                    required
                  />
                  <TaxNote>{t("linkedInvoiceNote") || "Required for credit notes to link to the original invoice."}</TaxNote>
                </FormGroup>
              </FormRow>
            )}

            {form.e_invoice && (
              <FormRow>
                <FormGroup>
                  <FormLabel className="required">{t("buyerReference") || "Buyer Reference (Leitweg-ID)"}</FormLabel>
                  <FormInput
                    type="text"
                    value={form.buyerReference}
                    onChange={(e) => updateField("buyerReference", e.target.value)}
                    placeholder={"e.g. 12345-67890-12"}
                    required
                  />
                  <TaxNote>{t("buyerReferenceNote") || "Required for public sector e-invoices in Germany."}</TaxNote>
                </FormGroup>
                <FormGroup>
                  <FormLabel>{t("vendorNumberAtCustomer") || "Vendor Number At Customer"}</FormLabel>
                  <FormInput
                    type="text"
                    value={form.vendorNumberAtCustomer}
                    onChange={(e) => updateField("vendorNumberAtCustomer", e.target.value)}
                    placeholder={"e.g. 987654"}
                  />
                  <TaxNote>{t("vendorNumberNote") || "Optional: Your supplier number at the buyer."}</TaxNote>
                </FormGroup>
              </FormRow>
            )}
          </FormSection>

          {/* ── Invoice Items ── */}
          <FormSection>
            <ItemsHeader>
              <SectionTitle>{t("invoiceItems") || "Invoice Items"}</SectionTitle>
              <AddItemButton type="button" onClick={addItem}>
                + {t("addItem") || "Add Item"}
              </AddItemButton>
            </ItemsHeader>

            {form.items.map((item, index) => (
              <ItemCard key={index}>
                <ItemNumber>
                  {t("item") || "Item"} #{index + 1}
                  {form.items.length > 1 && (
                    <RemoveItemButton
                      type="button"
                      onClick={() => removeItem(index)}
                    >
                      ✕
                    </RemoveItemButton>
                  )}
                </ItemNumber>
                <FormRow>
                  <FormGroup>
                    <FormLabel>{t("itemType") || "Item Type"}</FormLabel>
                    <FormSelect
                      value={item.itemType || "manual"}
                      onChange={(e) =>
                        handleItemTypeChange(index, e.target.value)
                      }
                    >
                      <option value="manual">
                        {t("manualItem") || "Manual Item"}
                      </option>
                      <option value="product">
                        {t("product") || "Product"}
                      </option>
                    </FormSelect>
                  </FormGroup>
                </FormRow>
                {item.itemType === "product" && (
                  <FormRow>
                    <FormGroup className="full">
                      <FormLabel>{t("product") || "Product"}</FormLabel>
                      <ProductSelect
                        api={billingApi}
                        value={item.productId || ""}
                        companyId={form.companyId}
                        date={new Date().toISOString()}
                        billingInfo={{
                          companyName: form.companyName,
                          firstName: form.firstName,
                          lastName: form.lastName,
                          email: form.email,
                          phone: form.phone,
                          address: {
                            street: form.street,
                            city: form.city,
                            postalCode: form.postalCode,
                            country: form.country,
                          },
                        }}
                        disabled={!form.companyId}
                        error={
                          !form.companyId
                            ? t("pleaseSelectCompanyFirst") ||
                              "Please select a company first."
                            : ""
                        }
                        onChange={(productId) =>
                          updateItem(index, "productId", productId)
                        }
                        onResolve={(product) =>
                          handleProductResolved(index, product)
                        }
                        onError={handleProductResolveError}
                      />
                      <TaxNote>
                        {form.companyId
                          ? t("productPriceWillBeResolvedOnSelection") ||
                            "Selecting a product fills the current snapshot price, but you can still edit it."
                          : t("selectCompanyBeforeProduct") ||
                            "Select a company before choosing a product."}
                      </TaxNote>
                    </FormGroup>
                  </FormRow>
                )}
                <FormRow>
                  <FormGroup className="full">
                    <FormLabel className="required">{t("description") || "Description"}</FormLabel>
                    <FormInput
                      type="text"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(index, "description", e.target.value)
                      }
                      placeholder={t("enterItemDescription") || "Enter item description"}
                      required
                    />
                  </FormGroup>
                </FormRow>
                <FormRow className="four-col">
                  <FormGroup>
                    <FormLabel className="required">{t("quantity") || "Quantity"}</FormLabel>
                    <FormInput
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={item.quantity ?? ""}
                      aria-label="Invoice item quantity"
                      onKeyDown={handleKeyDown}
                      onWheel={(e) => e.target.blur()}
                      onPaste={(e) => {
                        const paste = e.clipboardData.getData("text");
                        if (!/^\d*\.?\d*$/.test(paste)) e.preventDefault();
                      }}
                      onChange={(e) => {
                        const value = handleNumberChange(e);
                        updateItem(index, "quantity", value);
                      }}
                      onBlur={(e) => {
                        const value = handleBlurQuantity(e.target.value);
                        updateItem(index, "quantity", value);
                      }}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel className="required">
                      {(t("unitPrice") || "Unit Price") +
                        ` (${form.currency || "EUR"})`}
                    </FormLabel>
                    <FormInput
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={item.unitPrice ?? ""}
                      aria-label="Invoice item unit price"
                      onKeyDown={handleKeyDown}
                      onWheel={(e) => e.target.blur()}
                      onPaste={(e) => {
                        const paste = e.clipboardData.getData("text");
                        if (!/^\d*\.?\d*$/.test(paste)) e.preventDefault();
                      }}
                      onChange={(e) => {
                        const value = handleNumberChange(e);
                        updateItem(index, "unitPrice", value);
                      }}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>{t("tax") || "Tax"} (%)</FormLabel>
                    <FormInput
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={item.taxRate ?? ""}
                      aria-label="Invoice item tax rate"
                      onKeyDown={handleKeyDown}
                      onWheel={(e) => e.target.blur()}
                      onPaste={(e) => {
                        const paste = e.clipboardData.getData("text");
                        if (!/^\d*\.?\d*$/.test(paste)) e.preventDefault();
                      }}
                      onChange={(e) => {
                        const value = handleNumberChange(e);
                        updateItem(index, "taxRate", value);
                      }}
                    />
                  </FormGroup>
                  {/* <FormGroup>
                    <FormLabel>{t("total") || "Total"}</FormLabel>
                    <FormInput
                      type="text"
                      value={formatCurrency(calculateItemTotal(item))}
                      disabled
                    />
                  </FormGroup> */}
                </FormRow>
              </ItemCard>
            ))}

            {/* ── Totals ── */}
            <TotalsSection>
              <TotalRow>
                <TotalLabel>{t("subtotal") || "Subtotal"}:</TotalLabel>
                <TotalValue>{formatCurrency(subtotal)}</TotalValue>
              </TotalRow>
              <TotalRow>
                <TotalLabel>{t("tax") || "Tax"}:</TotalLabel>
                <TotalValue>{formatCurrency(taxAmount)}</TotalValue>
              </TotalRow>
              <TotalDivider />
              <TotalRow>
                <GrandTotalLabel>{t("totalAmount") || "Total Amount"}:</GrandTotalLabel>
                <GrandTotalValue>{formatCurrency(totalAmount)}</GrandTotalValue>
              </TotalRow>
              <TaxNote>
                {t("taxCalculatedPerLineNote") ||
                  "Tax is calculated per item based on the selected or entered tax rate."}
              </TaxNote>
            </TotalsSection>
          </FormSection>

          <FormSection>
            <SectionTitle>
              {t("recurringInvoiceOptional") || "Recurring Invoice (Optional)"}
            </SectionTitle>
            <FormRow>
              <FormGroup className="full">
                <FormLabel>{t("recurringInvoice") || "Recurring Invoice"}</FormLabel>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    marginTop: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.recurringInterval !== null}
                    onChange={(e) => handleRecurringToggle(e.target.checked)}
                  />
                  {t("enableRecurringInvoice") || "Enable recurring invoice"}
                </label>
              </FormGroup>
            </FormRow>

            {form.recurringInterval !== null && (
              <FormRow>
                <FormGroup>
                  <FormLabel className="required">
                    {t("interval") || "Interval"}
                  </FormLabel>
                  <FormSelect
                    value={form.recurringInterval}
                    onChange={(e) =>
                      handleRecurringIntervalChange(e.target.value)
                    }
                    required
                  >
                    <option value="">
                      {t("selectInterval") || "Select interval"}
                    </option>
                    <option value="monthly">
                      {t("monthly") || "Monthly"}
                    </option>
                    <option value="quarterly">
                      {t("quarterly") || "Quarterly"}
                    </option>
                    <option value="yearly">{t("yearly") || "Yearly"}</option>
                    <option value="custom_days">{t("custom") || "Custom"}</option>
                  </FormSelect>
                </FormGroup>

                {form.recurringInterval === "custom_days" && (
                  <FormGroup>
                    <FormLabel className="required">
                      {t("intervalDays") || "Interval Days"}
                    </FormLabel>
                    <FormInput
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={form.intervalDays ?? ""}
                      onChange={(e) =>
                        handleIntervalDaysChange(e.target.value)
                      }
                      placeholder={t("enterIntervalDays") || "Enter days"}
                      required
                    />
                    <TaxNote>
                      {t("customIntervalDaysNote") ||
                        "Enter the number of days between recurring invoices."}
                    </TaxNote>
                  </FormGroup>
                )}
              </FormRow>
            )}
          </FormSection>

          {/* ── Additional Notes ── */}
          <FormSection>
            <SectionTitle>{t("additionalNotes") || "Additional Notes"}</SectionTitle>
            <FormRow>
              <FormGroup className="full">
                <FormTextArea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder={t("additionalNotesPlaceholder") || "Add any additional notes or comments about this invoice..."}
                  rows={4}
                />
              </FormGroup>
            </FormRow>
          </FormSection>

          {/* ── Submit ── */}
          <ButtonRow>
            <SubmitButton type="submit" disabled={showLoader}>
              {showLoader
                ? t("creating") || "Creating..."
                : t("createInvoice") || "Create Invoice"}
            </SubmitButton>
          </ButtonRow>
        </CreateFormContainer>
        {showLoader && <Loader></Loader>}
      </Container>
    );
  }

  if(!(props.user.user?.authorisation?.toLowerCase())){
    return null;
  }

  // ── Render LIST view ──
  return (
    <Container className="noshadow column">
      {props.user.user?.authorisation?.toLowerCase() === "super" && <div style={{ display: "flex", gap: "20px", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
        <button
          onClick={() => setActiveTab("invoices")}
          style={{
            padding: "8px 16px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "invoices" ? "2px solid #3fc4fd" : "none",
            color: activeTab === "invoices" ? "#3fc4fd" : "#838894",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {t("invoices") || "Invoices"}
        </button>
        <button
          onClick={() => setActiveTab("templates")}
          style={{
            padding: "8px 16px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "templates" ? "2px solid #3fc4fd" : "none",
            color: activeTab === "templates" ? "#3fc4fd" : "#838894",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {t("recurringInvoices") || "Recurring Invoices"}
        </button>
      </div>}

      {props.user.user?.authorisation?.toLowerCase() === "super" ? (activeTab === "invoices" ? (
        <div style={{position: "relative"}}>
          <ListTable
            key="list-table-invoices"
            {...props}
            actions={[
              {
                id: "download-btn",
                element: "button",
                title: "download",
                icon: "download",
                type: "callback",
                callback: (itemDesc, rowData) => handleDownload(rowData),
              },
            ]}
            api={`invoice`}
            formMode="double"
            itemTitle={`invoiceNumber`}
            shortName={`invoice`}
            addPrivilege={false}
            delPrivilege={true}
            updatePrivilege={true}
            viewDetails={false}
            attributes={attributes}
          ></ListTable>
          {/* Custom "Create Invoice" button that opens the custom form */}
          <ButtonRow className={`floating ${sessionStorage.getItem("_lang")}`}>
            <AddItemButton onClick={() => setView("create")}>
              <GetIcon icon="add" fontSize={18} color="#ffffff" />
              <span>{t("createInvoice") || "Create Invoice"}</span>
            </AddItemButton>
          </ButtonRow>
        </div>
      ) : (
        <ListTable
          key="list-table-templates"
          {...props}
          actions={[
            {
              id: "toggle-active-btn",
              element: "button",
              title: "pauseResume",
              icon: "sync_alt",
              type: "callback",
              callback: (itemDesc, rowData) => handleToggleTemplateActive(rowData),
            },
            {
              id: "delete-template-btn",
              element: "button",
              title: "cancel",
              icon: "delete",
              type: "callback",
              callback: (itemDesc, rowData) => handleDeleteTemplate(rowData),
            },
          ]}
          api={`recurring-invoice`}
          formMode="double"
          itemTitle={`interval`}
          shortName={`recurringInvoice`}
          addPrivilege={false}
          delPrivilege={false}
          updatePrivilege={false}
          viewDetails={false}
          attributes={templateAttributes}
          refetchTableData={refetchTableData}
        ></ListTable>
      )) : (
        <ListTable
          key="list-table-invoices"
          {...props}
          actions={[
            {
              id: "download-btn",
              element: "button",
              title: "download",
              icon: "download",
              type: "callback",
              callback: (itemDesc, rowData) => handleDownload(rowData),
            },
          ]}
          api={`invoice`}
          formMode="double"
          itemTitle={`invoiceNumber`}
          shortName={`invoice`}
          addPrivilege={false}
          delPrivilege={false}
          updatePrivilege={false}
          viewDetails={false}
          attributes={companyAttributes}
        ></ListTable>
      )}



      {showLoader && <Loader></Loader>}
    </Container>
  );
};

// exporting the page with parent container layout..
export default Layout(Invoice);
